import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { AdminApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import { Field, PageHeader, PasswordInput, SimBadge, Spinner, StatusBadge } from "../../components/common";
import { DataTable } from "../../components/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { dateTime, phone } from "../../lib/format";

const ROLES = ["patient", "responder", "dispatcher", "cashier", "admin"];

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<any>({ role: "responder", name: "", username: "", email: "", phone: "", password: "", license_number: "", id_type: "", id_number: "" });
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const create = useMutation({
    mutationFn: () => AdminApi.createUser(f),
    onSuccess: () => { toast.success("Account created"); qc.invalidateQueries({ queryKey: ["admin"] }); onClose(); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); create.mutate(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="create-user-dialog">
        <DialogHeader><DialogTitle>Create staff account</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Role">
            <select className="fp-input" value={f.role} onChange={set("role")} data-testid="create-user-role-select">
              <option value="responder">Responder</option><option value="dispatcher">Dispatcher</option><option value="cashier">Cashier</option><option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Full name"><input className="fp-input" required value={f.name} onChange={set("name")} data-testid="create-user-name-input" /></Field>
          <Field label="Username" hint="Used to sign in"><input className="fp-input" required autoCapitalize="none" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value.toLowerCase() })} data-testid="create-user-username-input" /></Field>
          <Field label="Email"><input className="fp-input" type="email" required value={f.email} onChange={set("email")} data-testid="create-user-email-input" /></Field>
          <Field label="Mobile (+63)"><input className="fp-input" type="tel" required value={f.phone} onChange={set("phone")} data-testid="create-user-phone-input" /></Field>
          <Field label="Temporary password" hint="Share it privately; ask the user to change it after first sign-in."><PasswordInput required minLength={8} value={f.password} onChange={set("password")} data-testid="create-user-password-input" /></Field>
          {f.role === "responder" && (
            <>
              <p className="fp-label pt-1">Identity & credentials (for verification)</p>
              <Field label="EMT / professional license no."><input className="fp-input" value={f.license_number} onChange={set("license_number")} data-testid="create-user-license-input" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Government ID type"><input className="fp-input" placeholder="e.g. PhilSys, Driver's License" value={f.id_type} onChange={set("id_type")} data-testid="create-user-idtype-input" /></Field>
                <Field label="ID number"><input className="fp-input" value={f.id_number} onChange={set("id_number")} data-testid="create-user-idnumber-input" /></Field>
              </div>
              <p className="text-xs text-slate-500">New responders start as <strong>pending verification</strong> and cannot receive dispatches until approved.</p>
            </>
          )}
          <button className="fp-btn-primary w-full" disabled={create.isPending} data-testid="create-user-submit">Create account</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsers() {
  const { user: me } = useAuth();
  const qc = useQueryClient();
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["admin", "users", role, q], queryFn: () => AdminApi.users({ role: role || undefined, q: q || undefined }) });
  const update = useMutation({
    mutationFn: ({ id, body }: any) => AdminApi.updateUser(id, body),
    onSuccess: () => { toast.success("User updated"); qc.invalidateQueries({ queryKey: ["admin"] }); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const reset = async (id: string) => {
    const pw = window.prompt("New temporary password (min 8 characters)");
    if (!pw) return;
    try { await AdminApi.resetPassword(id, pw); toast.success("Password reset"); } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Users & roles" subtitle="Patients self-register. Staff accounts are created here."
        action={<button className="fp-btn-primary" onClick={() => setCreating(true)} data-testid="admin-create-user-button"><Plus size={16} /> Create staff account</button>} />
      <div className="flex flex-col gap-2 sm:flex-row">
        <input className="fp-input sm:max-w-xs" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-users-search" />
        <select className="fp-input sm:max-w-[180px]" value={role} onChange={(e) => setRole(e.target.value)} data-testid="admin-users-role-filter">
          <option value="">All roles</option>{ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      {isLoading ? <Spinner /> : (
        <DataTable testId="admin-users-table" rows={data || []} columns={[
          { key: "name", label: "Name", render: (u) => <div><p className="flex items-center gap-1.5 font-medium">{u.name}<SimBadge show={u.is_simulated} /></p><p className="text-xs text-slate-500">@{u.username} · {u.email}</p></div> },
          { key: "phone", label: "Mobile", render: (u) => phone(u.phone) },
          { key: "role", label: "Role", render: (u) => (
            <select className="h-9 rounded-lg border border-slate-300 px-2 text-xs" value={u.role} disabled={u.id === me.id}
              onChange={(e) => window.confirm(`Change ${u.name}'s role to ${e.target.value}?`) && update.mutate({ id: u.id, body: { role: e.target.value } })} data-testid={`user-role-select-${u.id}`}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>) },
          { key: "status", label: "Status", render: (u) => <StatusBadge status={u.status} /> },
          { key: "created", label: "Created", render: (u) => dateTime(u.created_at) },
          { key: "actions", label: "", render: (u) => u.id === me.id ? <span className="text-xs text-slate-400">You</span> : (
            <div className="flex gap-2">
              <button className="fp-btn-secondary min-h-[36px] px-2 text-xs" onClick={() => update.mutate({ id: u.id, body: { status: u.status === "suspended" ? "active" : "suspended" } })} data-testid={`user-toggle-status-${u.id}`}>
                {u.status === "suspended" ? "Reactivate" : "Suspend"}
              </button>
              <button className="fp-btn-secondary min-h-[36px] px-2 text-xs" onClick={() => reset(u.id)} data-testid={`user-reset-password-${u.id}`}>Reset password</button>
            </div>) },
        ]} />
      )}
      {creating && <CreateUserDialog onClose={() => setCreating(false)} />}
    </div>
  );
}
